import React, { FC, useContext, useEffect, useState } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from 'components/buttons/basicButton';

import { ToastStatus } from '../../../../../../constants/toastMessage.constants';

import { EditorContext } from 'context/EditorContext';

import { sanitizeForExcel } from '../../../../../../helpers/sanitize.helper';

import useMessageGenerationQueryV1 from 'hooks/formulas/useMessageGenerationQueryV1';

import { FormulaTestMessageFormValues } from 'models/formula.model';

import { toastService } from 'services/toast.service';

const TestFormulaCSVDownloadButton: FC = () => {
    const [csvDownload, setCsvDownload] = useState<boolean>(false);

    const {
        editorDetails: { formula, alternateMessagesCount },
    } = useContext(EditorContext);
    const { values } = useFormikContext<FormulaTestMessageFormValues>();

    const {
        data: alternateMessages,
        error,
        isLoading,
        isPending,
    } = useMessageGenerationQueryV1(
        Number(formula?.id),
        values?.message_content_id,
        alternateMessagesCount,
        values?.filters,
        { enabled: csvDownload },
    );

    useEffect(() => {
        if (csvDownload) {
            if (
                !(isLoading && isPending) &&
                alternateMessages?.length &&
                !error
            ) {
                try {
                    let headers: string[] = [];
                    const csvLines: string[] = [];

                    alternateMessages?.forEach((item, idx) => {
                        const flattenedContent: Record<string, any> = {};

                        for (const [key, value] of Object.entries(
                            item.content,
                        )) {
                            const cleanKey = key.split('--')[0];
                            flattenedContent[cleanKey] = value;
                        }

                        flattenedContent.image_url = item.image_url ?? '';

                        if (idx === 0) {
                            headers = Object.keys(flattenedContent);
                            csvLines.push(headers.join(','));
                        }

                        const csvRow = headers
                            .map((header) =>
                                JSON.stringify(
                                    sanitizeForExcel(
                                        flattenedContent[header] ?? '',
                                    ),
                                ),
                            )
                            .join(',');
                        csvLines.push(csvRow);
                    });

                    const csvString = csvLines.join('\n');

                    const blob = new Blob([csvString], {
                        type: 'text/csv;charset=utf-8;',
                    });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.setAttribute(
                        'download',
                        `${formula?.id}_alternate_messages_export.csv`,
                    );
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } catch {
                    toastService.showNotificationToast(
                        ToastStatus.Error,
                        'Error generating CSV',
                    );
                }
                setCsvDownload(false);
            }
            if (error) {
                toastService.showNotificationToast(
                    ToastStatus.Error,
                    'Error generating CSV',
                );
                setCsvDownload(false);
            }
            if (!isLoading && !isPending && !alternateMessages?.length) {
                toastService.showNotificationToast(
                    ToastStatus.Error,
                    'No data available for CSV download',
                );
                setCsvDownload(false);
            }
        }
    }, [
        alternateMessages,
        isPending,
        isLoading,
        error,
        formula?.id,
        csvDownload,
    ]);

    const handleCsvDownload = () => {
        setCsvDownload(true);
    };

    return (
        <BasicButton
            type="button"
            name="CSV Download"
            isDisabled={!alternateMessagesCount || alternateMessagesCount > 500}
            onClick={handleCsvDownload}
        />
    );
};

export default TestFormulaCSVDownloadButton;
